import {
  Button,
  Checkbox,
  Divider,
  Input,
  Modal,
  Radio,
  Space,
  Tag,
  Tooltip,
} from 'antd'
import { Filter, HelpCircle } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FixedSizeList as List } from 'react-window'
import styled from 'styled-components'
import { BaseSensorEntry } from 'types'

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 70vh;
`

const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const SectionTitle = styled.h4`
  color: ${({ theme }) => theme.forecast.text1};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const KeywordsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.5rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 4px;
  min-height: 40px;
  background: ${({ theme }) => theme.forecast.background3};
`

const TagsListWrapper = styled.div`
  flex: 1;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 4px;
  max-height: 300px;
  min-height: 200px;
  overflow: auto;
`

const TagInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-left: 12px;
`

const TagName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.text1};
`

const TagDetails = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 11px;
  color: ${({ theme }) => theme.forecast.text2};
`

const TagUnit = styled.span`
  color: ${({ theme }) => theme.forecast.primary};
`

const TagItem = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  background: ${({ $selected }) =>
    $selected ? 'rgba(0, 194, 144, 0.1)' : 'transparent'};
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
  transition: background 0.2s;

  &:hover {
    background: ${({ $selected }) =>
      $selected ? 'rgba(0, 194, 144, 0.15)' : 'rgba(255, 255, 255, 0.05)'};
  }

  &:last-child {
    border-bottom: none;
  }
`

const HelpText = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.forecast.text2};
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.25rem;
`

const StatusBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  font-size: 12px;
  color: ${({ theme }) => theme.forecast.text2};
`

interface AdvancedFilterModalProps {
  open: boolean
  onClose: () => void
  tags: BaseSensorEntry[]
  selectedTags: BaseSensorEntry[]
  currentFilters: {
    searchMode: 'AND' | 'OR'
    useWildcards: boolean
    selectedUnits: string[]
    keywords: string[]
  }
  onApply: (
    filters: {
      searchMode: 'AND' | 'OR'
      useWildcards: boolean
      selectedUnits: string[]
      keywords: string[]
    },
    selectedTags: BaseSensorEntry[]
  ) => void
}

export const AdvancedFilterModal: React.FC<AdvancedFilterModalProps> = ({
  open,
  onClose,
  tags,
  selectedTags,
  currentFilters,
  onApply,
}) => {
  const [localFilters, setLocalFilters] = useState(currentFilters)
  const [localSelectedTags, setLocalSelectedTags] =
    useState<BaseSensorEntry[]>(selectedTags)
  const [keywordInput, setKeywordInput] = useState('')

  useEffect(() => {
    if (open) {
      setLocalFilters(currentFilters)
      setLocalSelectedTags(selectedTags)
      setKeywordInput('')
    }
  }, [open, currentFilters, selectedTags])

  const filteredTags = useMemo(() => {
    let filtered = [...tags]

    if (localFilters.selectedUnits.length > 0) {
      filtered = filtered.filter(
        (tag) =>
          tag.data.unit && localFilters.selectedUnits.includes(tag.data.unit)
      )
    }

    // Include both the current input and saved keywords in filtering
    const allSearchTerms = [
      ...(keywordInput ? [keywordInput] : []),
      ...localFilters.keywords,
    ]

    if (allSearchTerms.length > 0) {
      filtered = filtered.filter((tag) => {
        const searchableText =
          `${tag.id} ${tag.data.name} ${tag.data.description || ''}`.toLowerCase()

        if (localFilters.useWildcards) {
          const patterns = allSearchTerms
            .map((term) => {
              const pattern = term
                .toLowerCase()
                .replace(/\*/g, '.*')
                .replace(/\?/g, '.')
              try {
                return new RegExp(pattern)
              } catch {
                return null
              }
            })
            .filter(Boolean) as RegExp[]

          if (localFilters.searchMode === 'AND') {
            return patterns.every((pattern) => pattern.test(searchableText))
          } else {
            return patterns.some((pattern) => pattern.test(searchableText))
          }
        } else {
          const lowerTerms = allSearchTerms.map((t) => t.toLowerCase())

          if (localFilters.searchMode === 'AND') {
            return lowerTerms.every((term) => searchableText.includes(term))
          } else {
            return lowerTerms.some((term) => searchableText.includes(term))
          }
        }
      })
    }

    return filtered
  }, [tags, keywordInput, localFilters])
  const handleAddKeyword = () => {
    if (
      keywordInput.trim() &&
      !localFilters.keywords.includes(keywordInput.trim())
    ) {
      setLocalFilters((prev) => ({
        ...prev,
        keywords: [...prev.keywords, keywordInput.trim()],
      }))
      setKeywordInput('')
    }
  }
  const handleClearKeywords = () => {
    setLocalFilters((prev) => ({
      ...prev,
      keywords: [],
    }))
    setKeywordInput('')
  }
  const handleRemoveKeyword = (keyword: string) => {
    setLocalFilters((prev) => ({
      ...prev,
      keywords: prev.keywords.filter((k) => k !== keyword),
    }))
  }

  const handleTagToggle = useCallback(
    (tag: BaseSensorEntry) => {
      const isSelected = localSelectedTags.some((t) => t.id === tag.id)

      if (isSelected) {
        setLocalSelectedTags((prev) => prev.filter((t) => t.id !== tag.id))
      } else {
        setLocalSelectedTags((prev) => [...prev, tag])
      }
    },
    [localSelectedTags]
  )

  const handleSelectAll = () => {
    setLocalSelectedTags(filteredTags)
  }

  const handleClearAll = () => {
    setLocalSelectedTags([])
  }

  const handleApply = () => {
    onApply(localFilters, localSelectedTags)
    onClose()
  }

  // Row renderer for virtualized list
  const TagRow = useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const tag = filteredTags[index]
      const isSelected = localSelectedTags.some((t) => t.id === tag.id)

      return (
        <div style={style}>
          <TagItem
            $selected={isSelected}
            onClick={() => handleTagToggle(tag)}
            style={{
              cursor: 'pointer',
            }}>
            <Checkbox checked={isSelected} />
            <TagInfo>
              <TagName>{tag.data.name}</TagName>
              <TagDetails>
                <span
                  style={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                  {tag.data.asset || tag.data.description}
                </span>
                {tag.data.unit && <TagUnit> {tag.data.unit}</TagUnit>}
              </TagDetails>
            </TagInfo>
          </TagItem>
        </div>
      )
    },
    [filteredTags, localSelectedTags, handleTagToggle]
  )

  return (
    <Modal
      title='Select PI Tags'
      open={open}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key='cancel' onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key='apply'
          type='primary'
          onClick={handleApply}
          icon={<Filter size={14} />}>
          Select Tags ({localSelectedTags.length} selected)
        </Button>,
      ]}>
      <ModalContent>
        <FilterSection>
          <SectionTitle>Keyword Filters</SectionTitle>
          <FilterRow>
            <Input
              placeholder='Type to filter tags or add persistent keyword...'
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onPressEnter={handleAddKeyword}
              style={{ flex: 1 }}
            />
            <Button onClick={handleClearKeywords}>Clear Keywords</Button>
          </FilterRow>

          <KeywordsContainer>
            {localFilters.keywords.length === 0 ? (
              <span style={{ color: '#888', fontSize: '12px' }}>
                No saved keywords (type above to filter in real-time)
              </span>
            ) : (
              localFilters.keywords.map((keyword) => (
                <Tag
                  key={keyword}
                  closable
                  onClose={() => handleRemoveKeyword(keyword)}>
                  {keyword}
                </Tag>
              ))
            )}
          </KeywordsContainer>

          <FilterRow>
            <Radio.Group
              value={localFilters.searchMode}
              onChange={(e) =>
                setLocalFilters((prev) => ({
                  ...prev,
                  searchMode: e.target.value,
                }))
              }
              size='small'>
              <Radio value='AND'>Match ALL keywords (AND)</Radio>
              <Radio value='OR'>Match ANY keyword (OR)</Radio>
            </Radio.Group>
          </FilterRow>

          <FilterRow>
            <Checkbox
              checked={localFilters.useWildcards}
              onChange={(e) =>
                setLocalFilters((prev) => ({
                  ...prev,
                  useWildcards: e.target.checked,
                }))
              }>
              Enable wildcards
            </Checkbox>
            <Tooltip title='Use * for any characters, ? for single character. Example: TEMP_* matches TEMP_001, TEMP_002, etc.'>
              <HelpCircle size={14} style={{ cursor: 'help', color: '#888' }} />
            </Tooltip>
          </FilterRow>

          {localFilters.useWildcards && (
            <HelpText>
              <HelpCircle size={12} />
              <span>* matches any characters, ? matches single character</span>
            </HelpText>
          )}
        </FilterSection>

        <Divider style={{ margin: '0.5rem 0' }} />

        <FilterSection style={{ flex: 1, minHeight: 0 }}>
          <FilterRow style={{ justifyContent: 'space-between' }}>
            <SectionTitle>Select Tags</SectionTitle>
            <Space>
              <Button size='small' onClick={handleSelectAll}>
                Select All Visible
              </Button>
              <Button size='small' onClick={handleClearAll}>
                Clear All
              </Button>
            </Space>
          </FilterRow>

          <TagsListWrapper>
            {filteredTags.length === 0 ? (
              <div
                style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>
                No tags match the current filters
              </div>
            ) : (
              <List
                height={200}
                itemCount={filteredTags.length}
                itemSize={50}
                width='100%'>
                {TagRow}
              </List>
            )}
          </TagsListWrapper>

          <StatusBar>
            <span>
              {filteredTags.length} tags found
              {keywordInput && ` (filtering: "${keywordInput}")`}
              {localFilters.keywords.length > 0 &&
                ` (${localFilters.keywords.length} saved keywords)`}
              {localFilters.selectedUnits.length > 0 &&
                ` (${localFilters.selectedUnits.length} units)`}
            </span>
            <span>{localSelectedTags.length} selected</span>
          </StatusBar>
        </FilterSection>
      </ModalContent>
    </Modal>
  )
}
